/* eslint-disable react-hooks/set-state-in-effect --
   This hook orchestrates a multi-step async draft-loading flow. Effects
   set loading flags synchronously then fetch + setState on resolution
   (the canonical "data fetching" use of effects). The rule's preferred
   shape (subscribe via callback) doesn't accommodate the loading-flag
   pattern; React docs explicitly allow this. */
// frontend/src/hooks/usePurchaseOrderDraft.js
//
// Draft state for the purchase-order form (PurchaseOrderForm) in its three
// modes — create from a recommendation, edit a draft, create from scratch —
// with every rule the form applies (cost, FX, tax, order rules, need-by).
//
// Reads and writes through `apiJson`, which throws `ApiError` on a refused
// answer and `NetworkError` when none came back: each request ends a failure
// in one catch, worded by `translateRequestError`.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DATA, useDataChanged } from '../queries/dataChanged';
import { useAuth, useIsShopify } from '../contexts/AuthContext';
import { useStoreData } from '../contexts/StoreDataContext';
import { useFormatters } from './useFormatters';
import { useSuppliers } from './useReferenceData';
import { formatApiError } from '../utils/parseApiError';
import { apiJson, translateRequestError } from '../queries/api';
import { formatItemLabel } from '../utils/itemLabel';

/**
 * The server's fixed sales window for Generate — a mirror of
 * `purchase_orders_engine.SALES_RATE_WINDOW_DAYS`, pinned to it by
 * `purchase_orders.test_suggest_lines`. The "Number of days" field opens at
 * it, so the first click reads "30 days of sales → 30 days of cover" (the
 * merchant changes only the cover), and the Based on sales hover names it.
 */
export const SALES_RATE_WINDOW_DAYS = 30;

/**
 * Where "generate from sales" reads sales AND stock from — a mirror of the
 * server's `PurchaseOrderViewSet.SUGGEST_SCOPES`. `location`: the deliver-to
 * location only. `store`: every location, for a merchant who receives at a
 * hub and transfers out. The two always switch together: a store-wide rate
 * netted against one shelf would order the whole store's demand into a hub
 * that already holds it elsewhere.
 */
export const SUGGEST_SCOPES = ['location', 'store'];

/**
 * What the last generation concluded, as `[key, values]` pairs the form
 * renders through `t()`. Three conclusions:
 * lines were generated (how many, plus how many sold items were left out as
 * covered or sized down by inventory in transit — each only when non-zero);
 * nothing sold (no rate to project); everything that sold is covered.
 * `title` is null for the first: the lines are the news there.
 */
export function describeSuggestion(suggestion) {
  const {
    scope, lineCount, soldCount, coveredCount, reducedByInTransitCount,
    windowDays, horizonDays,
  } = suggestion;
  const storeWide = scope === 'store';
  if (lineCount > 0) {
    const sentences = [[
      storeWide
        ? 'purchaseOrders.create.suggestion.generatedStore'
        : 'purchaseOrders.create.suggestion.generatedLocation',
      { count: lineCount, days: horizonDays },
    ]];
    if (coveredCount > 0) {
      sentences.push(['purchaseOrders.create.suggestion.generatedCovered', { count: coveredCount }]);
    }
    if (reducedByInTransitCount > 0) {
      sentences.push(['purchaseOrders.create.suggestion.generatedReduced', { count: reducedByInTransitCount }]);
    }
    return { title: null, sentences };
  }
  if (soldCount === 0) {
    return {
      title: 'purchaseOrders.create.suggestion.noSalesTitle',
      sentences: [[
        storeWide
          ? 'purchaseOrders.create.suggestion.noSalesStore'
          : 'purchaseOrders.create.suggestion.noSalesLocation',
        { window: windowDays },
      ]],
    };
  }
  return {
    title: 'purchaseOrders.create.suggestion.allCoveredTitle',
    sentences: [
      [
        storeWide
          ? 'purchaseOrders.create.suggestion.allCoveredStore'
          : 'purchaseOrders.create.suggestion.allCoveredLocation',
        { count: soldCount, window: windowDays, days: horizonDays },
      ],
      ['purchaseOrders.create.suggestion.allCoveredFooter'],
    ],
  };
}

/**
 * A line on the order as a picker row, in the catalog row's names, so the
 * picker opens with the line ticked at its own quantity and shows the SKU the
 * order holds. `applyPickedRows` reads the quantity back — the one line field
 * the picker edits.
 */
export function pickerRowFromLine(line) {
  return {
    item_id: line.item_id,
    supplier_sku: line.supplier_sku,
    _draftQty: line.quantity,
  };
}

/**
 * The supplier's order rules a line's quantity breaks, each `{rule, value}`:
 * `moq` (below the minimum order quantity), `max_oq` (above the maximum) and
 * `order_multiple` (not a whole number of multiples). A null rule is one the
 * supplier does not set; a null quantity is a picker row nobody typed on, and
 * breaks nothing. The one home of the check: the quantity cells of the picker
 * and the lines tint and name what this returns, and the form's banner counts
 * it.
 */
export function brokenOrderRules(line) {
  const { quantity, moq, max_oq: maxOq, order_multiple: multiple } = line;
  const broken = [];
  if (quantity == null) return broken;
  if (moq != null && quantity < moq) broken.push({ rule: 'moq', value: moq });
  if (maxOq != null && quantity > maxOq) broken.push({ rule: 'max_oq', value: maxOq });
  if (multiple != null && quantity % multiple !== 0) {
    broken.push({ rule: 'order_multiple', value: multiple });
  }
  return broken;
}

/** The quantity a picked row orders: what was typed, else one. */
function pickedQuantity(row) {
  return row._draftQty > 0 ? row._draftQty : 1;
}

/**
 * A draft line from a `/supplier_catalog/`-shaped row at `quantity`. The one
 * mapping from what the endpoint sends to what the line editors and the
 * save payload read — the picker (`applyPickedRows`) and the sales
 * suggestion (`generateLinesFromSales`) both go through it, so a generated
 * line carries exactly what a picked one does.
 */
export function lineFromCatalogRow(row, quantity) {
  return {
    item_id: row.item_id,
    item_code: row.item_code,
    item_name: row.item_name,
    sku: row.sku || '',
    image_url: row.image_url || '',
    item_location_id: row.item_location_id,
    quantity,
    // Cost is MONEY. A catalog row whose (item, supplier) relation carries no
    // price arrives as null and STAYS null: substituting 0 would turn "we
    // don't know what this costs" into a total the merchant can send to a
    // supplier. The line saves unpriced — a merchant waiting on a quote has
    // no price to type — and the order has no total until it is priced
    // (unpricedLineCount).
    cost: row.current_cost_price_supplier_ccy == null
      ? null
      : Number(row.current_cost_price_supplier_ccy),
    supplier_sku: row.supplier_sku,
    // Pre-seed Tax % from the resolved chain on the catalog row:
    //   Item.default_tax_rate → Supplier.default_tax_rate (resolved
    //   server-side).
    // 0 is a legitimate rate (zero-rated / reverse-charge) — use `?? null` so
    // a stored zero survives. Null shows "—" in the selected-lines grid.
    tax_rate_percentage: row.default_tax_rate ?? null,
    moq: row.moq,
    max_oq: row.max_oq,
    order_multiple: row.order_multiple,
  };
}

/**
 * A line's extended total in the supplier's currency, or `null` when the line
 * carries no cost.
 *
 * The one home for the null-cost rule on the money path. `cost` is null when
 * the (item, supplier) relation has no price — `supplier_catalog` sends
 * `current_cost_price_supplier_ccy: null` for those rows (the `_catalog_row`
 * build in po_viewset.py). A zero there would read as a price. The form and
 * the totals below route through this, so the answer cannot differ between
 * the cell a merchant reads and the sum they act on.
 *
 * `quantity || 0` is not the same shape: quantity has a floor of 1 in every
 * editor and is never null, so the guard is arithmetic hygiene, not a
 * substituted price.
 */
export function lineExtendedTotal(line) {
  if (line?.cost == null) return null;
  return (line.quantity || 0) * line.cost;
}

/** A null rate and a 0% rate both add no tax. */
function lineIsTaxed(line) {
  return line.tax_rate_percentage != null && line.tax_rate_percentage > 0;
}

// -----------------------------------------------------------------------------
// Arguments
// -----------------------------------------------------------------------------
//   open                 — bool, drives mount-reset effect
//   recommendationId     — optional rec id for pre-fill (create-from-rec mode)
//   excludedLineIds      — optional list of rec line ids to skip
//   purchaseOrderId      — optional PO id for pre-fill (edit-draft mode).
//                          Mutually exclusive with recommendationId in
//                          practice: the caller passes one or the other.
//                          When set, the hook:
//                            - pre-fills from /draft_payload/ (header + lines
//                              restored as saved, including FX source);
//                            - skips the auto-destination and
//                              supplier-auto-FX-default effects (saved state
//                              is authoritative);
//                            - routes save() to POST /update_draft/ instead
//                              of POST / for create.
//   onSaved              — ({ id, poNumber, status, messages }) => void —
//                          `id` is the saved header's purchase_order_header_id,
//                          so a caller can open its details.
//   onClose              — () => void
//   onRecommendationGone — optional () => void. Fired when the rec pre-fill
//                          returns 404: recommendation ids are valid only
//                          within one calculation run (every run deletes all
//                          pending recs and recreates them with new ids —
//                          purchase_orders/services.py
//                          _replace_pending_recommendations), so a dead id
//                          means "recalculated since Review & create was clicked".
//                          The caller closes the form and refreshes its list;
//                          no in-form error is set. Without it the 404 falls
//                          through to the generic pre-fill error.
//
// The store's currency, its locations and the supplier list are the hook's own
// reads (AuthContext, StoreDataContext, useSuppliers), the same sources the
// form renders from.
// -----------------------------------------------------------------------------

// The supplier's PO notes template (api.models.Supplier.po_notes_template) —
// what "Notes to supplier" starts as on a new order to it. No supplier chosen
// yet has no template.
function notesTemplateFor(suppliers, supplierId) {
  const supplier = suppliers.find((s) => s.supplier_id === supplierId);
  return supplier ? supplier.po_notes_template : '';
}

export function usePurchaseOrderDraft({
  open,
  recommendationId = null,
  excludedLineIds = [],
  purchaseOrderId = null,
  onSaved,
  onClose,
  onRecommendationGone = null,
}) {
  const { t } = useTranslation();
  // `manualRate` is the text of the rate field, in the merchant's number
  // format: read with `parseNumber`, and a rate from the server enters it
  // through `rateText`, never as the wire string (whose '.' a Dutch-format
  // parser reads as a thousands separator).
  const { parseNumber, formatNumber } = useFormatters();
  // Six places: what ExchangeRate.rate and the PO header's ExchangeRate store.
  const rateText = useCallback((rate) => formatNumber(Number(rate), 6), [formatNumber]);
  const { selectedStore } = useAuth();
  const storeCurrency = selectedStore.currency;
  const changed = useDataChanged();
  const isShopify = useIsShopify();
  const { locations, error: locationsLoadError } = useStoreData();
  const locationsError = locationsLoadError
    ? t('common.locationsLoadFailed')
    : null;
  // Every supplier, not the first page: this list is what the merchant picks
  // the PO's supplier FROM, and suppliers are auto-created per Shopify vendor
  // string, so the count grows with the catalog. A walk too long to finish
  // ends in `suppliersLoadError` like a failed request.
  const {
    data: suppliers, loading: suppliersLoading, error: suppliersLoadError,
  } = useSuppliers();
  const suppliersError = suppliersLoadError
    ? t('purchaseOrders.create.suppliersLoadFailed')
    : null;
  // The pre-fill effects wait for the list, but run once per open: a refetch
  // (tab focus, another screen's supplier edit) hands back a new array, and
  // re-running a pre-fill would overwrite what the merchant has edited since.
  // They depend on this flag and read the list through the ref.
  const suppliersReady = suppliers.length > 0;
  const suppliersRef = useRef(suppliers);
  useEffect(() => {
    suppliersRef.current = suppliers;
  }, [suppliers]);

  // --- Form state ---
  const [supplierId, setSupplierId] = useState('');
  const [locationId, setLocationId] = useState('');
  // The delivery date field. `proposedNeedByDate` is the date the server
  // would stamp on these lines if they were issued today (POST
  // expected-delivery/); `needByDate` is the merchant's own date — typed, or
  // the recommendation's (system-calculated or edited on the Purchase
  // recommendations grid) — null while the field shows the proposed one.
  // Only an issuing save stores a date: a draft stores none
  // (po_header_need_by_iff_issued), so a draft reopens on the proposed date.
  const [needByDate, setNeedByDate] = useState(null);
  const [proposedNeedByDate, setProposedNeedByDate] = useState(null);
  const [proposedNeedByError, setProposedNeedByError] = useState(null);
  const [notes, setNotes] = useState('');
  const [supplierNotes, setSupplierNotes] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [selectedLines, setSelectedLines] = useState([]);
  // The existing PO number when editing a draft, for the form's heading.
  // Empty in create flows.
  const [poNumber, setPoNumber] = useState('');

  // --- FX state ---
  const [fx, setFx] = useState(null); // resolve_fx: { rate, rate_date, locked }
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);
  const [autoFx, setAutoFx] = useState(true);
  const [manualRate, setManualRate] = useState('');

  // --- Submit state ---
  // The status the running save was asked for ('draft' | 'open'), null when
  // idle: the form spins the one button the merchant pressed.
  const [savingAs, setSavingAs] = useState(null);
  const saving = savingAs !== null;
  const [error, setError] = useState(null);
  // The save the server answered with the cost question instead of saving:
  // `{ targetStatus, lockRate, changes }`, null when nothing is asked. The
  // merchant's answer re-sends that save (answerCostQuestion).
  const [costQuestion, setCostQuestion] = useState(null);

  // --- Derived values that depend only on state + args ---
  const selectedSupplier = useMemo(
    () => suppliers.find(s => s.supplier_id === supplierId) || null,
    [suppliers, supplierId]
  );
  // Choosing a supplier seeds "Notes to supplier" with its template. A later
  // supplier change swaps the template only while the notes still read the
  // previous one, so text the merchant wrote is never replaced.
  const chooseSupplier = useCallback((id) => {
    const previousTemplate = notesTemplateFor(suppliers, supplierId);
    setSupplierId(id);
    setSupplierNotes((current) => (
      current === previousTemplate ? notesTemplateFor(suppliers, id) : current
    ));
  }, [suppliers, supplierId]);

  const supplierCurrencyCode = selectedSupplier?.default_currency?.currency_code || null;
  const supplierCurrencySymbol = selectedSupplier?.default_currency?.symbol || '';
  const isMismatchedCcy = !!(supplierCurrencyCode && storeCurrency && supplierCurrencyCode !== storeCurrency);

  // -------------------------------------------------------------------------
  // Effect 1 — Reset form on open
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    setSupplierId('');
    setLocationId('');
    setNeedByDate(null);
    setNotes('');
    setSupplierNotes('');
    setReferenceNumber('');
    setTaxInclusive(false);
    setSelectedLines([]);
    setFx(null);
    setFxError(null);
    setManualRate('');
    setAutoFx(true);
    setError(null);
    setPoNumber('');
  }, [open]);

  // The form's banners say only "couldn't load"; the log tells a 503 from a
  // list that outgrew the walk.
  useEffect(() => {
    if (suppliersLoadError) console.error('PO draft: suppliers load failed', suppliersLoadError);
  }, [suppliersLoadError]);
  useEffect(() => {
    if (locationsLoadError) console.error('PO draft: locations load failed', locationsLoadError);
  }, [locationsLoadError]);

  // -------------------------------------------------------------------------
  // Effect 3 — Auto-default the destination. First active location wins when
  // multiple exist. Skipped when recommendationId is set (rec pre-fill owns
  // the field) OR when purchaseOrderId is set (draft pre-fill owns it).
  // Only fires when the form opens, not on every locations change, so a
  // deliberate clear isn't undone.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open || locationId || locations.length === 0 || recommendationId || purchaseOrderId) return;
    const firstActive = locations.find(l => l.is_active !== false) || locations[0];
    if (firstActive) setLocationId(firstActive.location_id);
  }, [open, locations, locationId, recommendationId, purchaseOrderId]);

  // The excluded set as an identity-stable value.
  //
  // `excludedLineIds` is a NEW ARRAY on every render of the calling form (a
  // prop default, or an inline literal). The pre-fill effect below depends on
  // this string instead, which compares by value; depending on the array would
  // refetch /from_recommendation/ on every render and overwrite the merchant's
  // edits with the server's copy (usePurchaseOrderDraft.recPrefill.test.jsx).
  const excludedLineIdsKey = excludedLineIds.join(',');

  // Always-fresh ref so the pre-fill effect below never depends on the
  // callback's identity — an unstable caller callback in its dep list would
  // re-run the effect and refetch /from_recommendation/. Mirrored in an
  // effect (not during render); the pre-fill effect only reads it after its
  // own fetch resolves, which is always later.
  const onRecommendationGoneRef = useRef(onRecommendationGone);
  useEffect(() => {
    onRecommendationGoneRef.current = onRecommendationGone;
  }, [onRecommendationGone]);

  // -------------------------------------------------------------------------
  // Effect 4 — Pre-fill from recommendation. Waits for the suppliers list to
  // load before setting supplierId (otherwise the Select can't resolve the
  // label, and the supplier's notes template is not known yet). Gated on
  // !purchaseOrderId so the two pre-fill modes don't race to set supplierId.
  //
  // Runs ONCE per (recommendation, excluded set): the pre-fill seeds the form
  // and the merchant owns it from there.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !recommendationId || purchaseOrderId || !suppliersReady) return;
    (async () => {
      try {
        const params = new URLSearchParams({ recommendation_id: String(recommendationId) });
        if (excludedLineIdsKey) {
          params.append('excluded_line_ids', excludedLineIdsKey);
        }
        const data = await apiJson.get(`/api/po-calculations/purchase-orders/from_recommendation/?${params}`);
        setSupplierId(data.supplier_id);
        setSupplierNotes(notesTemplateFor(suppliersRef.current, data.supplier_id));
        setLocationId(data.location_id);
        setNeedByDate(data.need_by_date ?? null);
        // Lines arrive fully hydrated — the from_recommendation endpoint sends
        // cost / supplier_sku off the (item, PO supplier) relation and tax off
        // the Item, so there's no follow-up catalog cross-reference.
        setSelectedLines(data.lines.map(line => ({
          item_id: line.item_id,
          item_code: line.item_code,
          item_name: line.item_name,
          sku: line.sku || '',
          image_url: line.image_url || '',
          item_location_id: line.item_location_id,
          quantity: line.recommended_quantity,
          // No `?? 0`: the endpoint sends null for a line nobody has priced,
          // and this line decides whether the merchant sees the missing price
          // or a free order. Null stays null — the same as a picked catalog
          // item with no price.
          cost: line.cost,
          supplier_sku: line.supplier_sku,
          tax_rate_percentage: line.tax_rate_percentage,
          // The supplier's order rules for the item (brokenOrderRules).
          moq: line.moq,
          max_oq: line.max_oq,
          order_multiple: line.order_multiple,
          // Item reassigned to another supplier since the rec was generated —
          // its cost is in that supplier's currency, so the save gate rejects
          // it. Surface it as a blocking warning; the merchant removes the line.
          supplier_mismatch: !!line.supplier_mismatch,
        })));
      } catch (err) {
        // 404 = the id no longer exists, i.e. a calculation run replaced
        // every pending recommendation since Review & create was clicked (ids are
        // per-run — see the onRecommendationGone arg note). A staleness
        // signal for the caller, not a form error.
        if (err.status === 404 && onRecommendationGoneRef.current) {
          onRecommendationGoneRef.current();
          return;
        }
        console.error('PO rec pre-fill failed:', err.message);
        setError(translateRequestError(err, t, 'purchaseOrders.create.recPrefillFailed'));
      }
    })();
  }, [open, recommendationId, purchaseOrderId, excludedLineIdsKey, suppliersReady, t]);

  // -------------------------------------------------------------------------
  // Effect 4b — Pre-fill from existing draft PO (edit mode). Same shape as
  // the rec pre-fill, different endpoint and different line semantics:
  //   - cost / tax_rate_percentage come from the saved draft (its committed
  //     values are authoritative even if the supplier catalog has since
  //     drifted), whereas the rec prefill reads them live. supplier_sku is
  //     read live off the (item, PO supplier) relation in both — the PO save
  //     writes it there, and a line stores none. Both arrive fully hydrated,
  //     so neither needs a catalog backfill.
  //   - autoFx + manualRate are restored from the saved FX source so the
  //     merchant sees the same FX mode they last committed.
  //   - taxInclusive, notes, supplierNotes, referenceNumber likewise
  //     restored.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !purchaseOrderId || !suppliersReady) return;
    (async () => {
      try {
        const data = await apiJson.get(`/api/po-calculations/purchase-orders/${purchaseOrderId}/draft_payload/`);
        setSupplierId(data.supplier_id);
        setLocationId(data.location_id);
        setNotes(data.notes || '');
        setSupplierNotes(data.supplier_notes || '');
        setReferenceNumber(data.reference_number || '');
        setTaxInclusive(!!data.tax_inclusive);
        setPoNumber(data.po_number || '');
        // Restore FX mode from the saved source. 'manual' → autoFx=false
        // and pre-seed manualRate with the saved rate. Anything else
        // (auto / empty for same-ccy) → autoFx=true; Effect 7 fetches the
        // newest cached rate shortly after.
        if (data.exchange_rate_source === 'manual') {
          setAutoFx(false);
          if (data.exchange_rate != null) {
            setManualRate(rateText(data.exchange_rate));
          }
        } else {
          setAutoFx(true);
        }
        // Lines fully hydrated from the draft's saved values (authoritative).
        setSelectedLines(data.lines.map(line => ({
          item_id: line.item_id,
          item_code: line.item_code,
          item_name: line.item_name,
          sku: line.sku || '',
          image_url: line.image_url || '',
          item_location_id: line.item_location_id,
          quantity: line.recommended_quantity,
          // Same rule as the rec pre-fill above: the price the draft was saved
          // with, or nothing — a draft saved unpriced reopens unpriced.
          cost: line.cost,
          supplier_sku: line.supplier_sku,
          tax_rate_percentage: line.tax_rate_percentage,
          moq: line.moq,
          max_oq: line.max_oq,
          order_multiple: line.order_multiple,
        })));
      } catch (err) {
        console.error('PO draft pre-fill failed:', err.message);
        setError(translateRequestError(err, t, 'purchaseOrders.edit.prefillFailed'));
      }
    })();
  }, [open, purchaseOrderId, suppliersReady, rateText, t]);

  // -------------------------------------------------------------------------
  // Effect 6 — Initialize autoFx from supplier's lock state.
  //   No lock (fixed_fx_rate is null) → autoFx=true (the newest cached rate;
  //     merchant can untick to type a manual rate).
  //   Has lock → autoFx=false (lock is the supplier's chosen rate; merchant
  //     can tick "Use latest exchange rate" to override for this single PO).
  // Skipped in edit mode — draft pre-fill (Effect 4b) restores the saved
  // FX source, and overwriting it here would forget the merchant's last
  // manual-vs-auto choice on reopen.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (purchaseOrderId) return;
    if (!selectedSupplier) return;
    setAutoFx(selectedSupplier.fixed_fx_rate == null);
  }, [selectedSupplier, purchaseOrderId]);

  // -------------------------------------------------------------------------
  // FX fetch — memoized so effect 8 can depend on it. refresh=true hits the
  // provider via the backend's resolve_fx endpoint.
  // -------------------------------------------------------------------------
  const loadFx = useCallback(async (refresh = false) => {
    if (!supplierId || !isMismatchedCcy) {
      setFx(null);
      setFxError(null);
      return;
    }
    setFxLoading(true);
    setFxError(null);
    try {
      const data = await apiJson.post('/api/po-calculations/purchase-orders/resolve_fx/', {
        supplier_id: supplierId,
        refresh,
      });
      setFx(data);
      if (data.rate && !manualRate) {
        setManualRate(rateText(data.rate));
      }
    } catch (err) {
      console.error('resolve_fx failed:', err.message);
      setFx(null);
      setFxError(translateRequestError(err, t, 'purchaseOrders.create.fxUnavailable'));
    } finally {
      setFxLoading(false);
    }
  }, [supplierId, isMismatchedCcy, manualRate, rateText, t]);

  // -------------------------------------------------------------------------
  // Effect 7 — Fetch FX whenever the loadFx identity changes (i.e. supplier
  // or cross-ccy state changed).
  // -------------------------------------------------------------------------
  useEffect(() => {
    loadFx(false);
  }, [loadFx]);

  // A cleared field ('') goes back to the proposed date.
  const chooseNeedByDate = useCallback((day) => setNeedByDate(day === '' ? null : day), []);

  // -------------------------------------------------------------------------
  // Effect 8 — The proposed delivery date, re-read whenever the supplier, the
  // destination or the set of items changes (a quantity edit moves no date).
  // A reply that arrives after a newer change is dropped.
  // -------------------------------------------------------------------------
  const lineItemIdsKey = selectedLines.map(line => line.item_id).sort((a, b) => a - b).join(',');
  useEffect(() => {
    setProposedNeedByDate(null);
    setProposedNeedByError(null);
    if (!open || !supplierId || !locationId) return undefined;
    let current = true;
    (async () => {
      try {
        const data = await apiJson.post('/api/po-calculations/purchase-orders/expected-delivery/', {
          supplier_id: supplierId,
          location_id: locationId,
          item_ids: lineItemIdsKey ? lineItemIdsKey.split(',').map(Number) : [],
        });
        if (current) setProposedNeedByDate(data.need_by_date);
      } catch (err) {
        if (!current) return;
        console.error('expected-delivery failed:', err.message);
        setProposedNeedByError(translateRequestError(err, t, 'purchaseOrders.messages.issueDateFailed'));
      }
    })();
    return () => { current = false; };
  }, [open, supplierId, locationId, lineItemIdsKey, t]);

  // -------------------------------------------------------------------------
  // Line actions
  // -------------------------------------------------------------------------
  // The picker's confirm. `rows` is every item ticked in it — the order's own
  // lines open ticked (pickerRowFromLine) — each with what was typed on it.
  // A line left out was unticked and goes; a line still ticked takes the
  // picker's quantity and keeps everything else; a new row becomes a line
  // after them.
  const applyPickedRows = useCallback((rows) => {
    setSelectedLines(prev => {
      const picked = new Map(rows.map(row => [String(row.item_id), row]));
      const onOrder = new Set(prev.map(line => String(line.item_id)));
      const kept = prev
        .filter(line => picked.has(String(line.item_id)))
        .map(line => {
          const quantity = pickedQuantity(picked.get(String(line.item_id)));
          return quantity === line.quantity ? line : { ...line, quantity };
        });
      const added = rows
        .filter(row => !onOrder.has(String(row.item_id)))
        .map(row => lineFromCatalogRow(row, pickedQuantity(row)));
      // An Apply that changed nothing keeps the array itself: generateWouldDiscard
      // reads identity, and an untouched Generate result must stay one.
      if (!added.length && kept.length === prev.length && kept.every((line, i) => line === prev[i])) {
        return prev;
      }
      return [...kept, ...added];
    });
  }, []);

  // ── Generate ────────────────────────────────────────────────────────
  // The form's alternative to typing quantities: `/suggest_lines/` returns
  // the supplier's items that sold over the last `sales_window_days` — at
  // the destination or across the store, per `suggestionScope` — each with
  // a `suggested_quantity` that covers `suggestionDays` at that rate net of
  // the stock in the same scope (the math is server-side,
  // purchase_orders_engine.suggest_from_sales_rate). The rows are catalog
  // rows, so they become lines through the same mapping a picked item does.
  // Generating REPLACES the lines: the result is "the order the sales rate
  // asks for", and merging it over hand-picked lines would leave a mix
  // nobody chose.
  //
  // `suggestion` records the last outcome — the window, horizon and scope
  // read, plus the server's conclusion counts (`describeSuggestion` turns
  // them into sentences) — so the form can tell "nothing sold" from
  // "everything is covered" when the result is empty (an empty order is a
  // conclusion about the merchant's sales and inventory, not an error). It
  // clears when the supplier or destination changes, because it described
  // the old pair.
  const [suggestionDays, setSuggestionDays] = useState(SALES_RATE_WINDOW_DAYS);
  const [suggestionScope, setSuggestionScope] = useState('location');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  // The lines the last generation set, by identity. `generateWouldDiscard`
  // below compares the current lines against it: every edit, pick or removal
  // makes a new array, so "same array" means "the last result, untouched".
  // Forgotten together with `suggestion`: lines sized for another pair are
  // not a result to re-run over silently.
  const [suggestedLines, setSuggestedLines] = useState(null);

  useEffect(() => {
    setSuggestion(null);
    setSuggestedLines(null);
  }, [open, supplierId, locationId]);

  const generateLinesFromSales = useCallback(async () => {
    setSuggesting(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        supplier_id: String(supplierId),
        location_id: String(locationId),
        days: String(suggestionDays),
        scope: suggestionScope,
      });
      const data = await apiJson.get(`/api/po-calculations/purchase-orders/suggest_lines/?${params}`);
      const lines = data.results.map(
        (row) => lineFromCatalogRow(row, row.suggested_quantity),
      );
      setSuggestedLines(lines);
      setSelectedLines(lines);
      setSuggestion({
        windowDays: data.sales_window_days,
        horizonDays: data.horizon_days,
        scope: data.scope,
        lineCount: data.results.length,
        soldCount: data.sold_count,
        coveredCount: data.covered_count,
        reducedByInTransitCount: data.reduced_by_in_transit_count,
      });
    } catch (err) {
      console.error('Generate lines from sales failed:', err.message);
      // A refusal's body feeds the copy, so it can name the bound the server
      // refused at (`{{max}}` for invalid_suggest_horizon).
      setError(translateRequestError(err, t, 'purchaseOrders.create.suggestion.failed'));
    } finally {
      setSuggesting(false);
    }
  }, [supplierId, locationId, suggestionDays, suggestionScope, t]);

  const removeLine = useCallback((itemId) => {
    setSelectedLines(prev => prev.filter(line => line.item_id !== itemId));
  }, []);

  // Explicit per-line patch, called from the grid's `onCellValueChanged` to
  // re-sync hook state after the valueSetter has mutated `params.data` in
  // place. `patch` is a shallow object of fields to merge onto the matching
  // line.
  const updateLine = useCallback((itemId, patch) => {
    setSelectedLines(prev => prev.map(line =>
      line.item_id === itemId ? { ...line, ...patch } : line
    ));
  }, []);

  // -------------------------------------------------------------------------
  // Derived totals + validation
  // -------------------------------------------------------------------------
  const { subtotal, taxTotal, total } = useMemo(() => {
    let sub = 0;
    let tx = 0;
    selectedLines.forEach(line => {
      // A line with no cost contributes NOTHING to the totals rather than
      // contributing a zero. The form shows the totals as unknown while
      // `unpricedLineCount` is non-zero, so the merchant never reads a sum
      // that quietly omits a line as the whole.
      const lineTotal = lineExtendedTotal(line);
      if (lineTotal === null) return;
      if (lineIsTaxed(line)) {
        const r = line.tax_rate_percentage / 100;
        if (taxInclusive) {
          const taxPortion = lineTotal - lineTotal / (1 + r);
          sub += lineTotal - taxPortion;
          tx += taxPortion;
        } else {
          sub += lineTotal;
          tx += lineTotal * r;
        }
      } else {
        sub += lineTotal;
      }
    });
    return { subtotal: sub, taxTotal: tx, total: sub + tx };
  }, [selectedLines, taxInclusive]);
  // Read on the rate, not on taxTotal: an unpriced line carries no tax amount
  // yet, but its rate already decides whether "prices include tax" matters.
  const hasTaxedLine = selectedLines.some(lineIsTaxed);

  // The typed rate, read once: the order converts at it, saves it and, when
  // the merchant says so, locks it onto the supplier — the same number each
  // time.
  const typedRate = parseNumber(manualRate);

  const activeRate = useMemo(() => {
    if (!isMismatchedCcy) return 1;
    if (autoFx) return fx?.rate ? parseFloat(fx.rate) : null;
    return isNaN(typedRate) ? null : typedRate;
  }, [isMismatchedCcy, autoFx, fx, typedRate]);

  // Whether saving may offer to lock the typed rate as the supplier's
  // default: the order converts at a typed rate the supplier does not already
  // carry as its lock.
  const offerLock = isMismatchedCcy && !autoFx && typedRate > 0
    && (selectedSupplier.fixed_fx_rate == null
      || Math.abs(typedRate - Number(selectedSupplier.fixed_fx_rate)) > 1e-6);

  // Lines with no price yet — picked from a catalog whose (item, supplier)
  // relation has none, or reopened from a draft saved that way. Not a block:
  // a merchant whose supplier quotes off the draft has nothing to type. The
  // form shows the totals as unknown while this is non-zero, and the backend
  // saves the line with Cost NULL and the header with no total. (A typed
  // cost can also become the supplier's price — the cost question in save.)
  const unpricedLineCount = selectedLines.filter(l => l.cost == null).length;

  // The store-currency equivalent of a total that does not exist yet does
  // not exist either.
  const storeTotal = activeRate != null && unpricedLineCount === 0 ? total * activeRate : null;

  // Lines whose quantity breaks one of the supplier's order rules, shown
  // live so the merchant can fix the quantity before saving. A warning, not a
  // block: ordering outside a rule can be deliberate.
  const orderRuleLineCount = selectedLines.filter(
    l => brokenOrderRules(l).length > 0,
  ).length;

  // The lines whose supplier drifted away from this PO's supplier, named the
  // way the merchant reads them (`formatItemLabel` — the one home for that
  // rule): on a Shopify store by product name + SKU, because `item_code` there
  // is the variant id's numeric suffix, a number the merchant has never seen. Unlike a missing tax rate (a soft warning), these hard-block save —
  // the server rejects them (po_line_supplier_mismatch) because their cost is
  // in another supplier's currency. The merchant clears the block by removing
  // the lines.
  const supplierMismatchItems = selectedLines
    .filter(l => l.supplier_mismatch)
    .map(l => formatItemLabel(l, isShopify));

  // Whether a generation now would throw away the merchant's work: lines
  // picked by hand, loaded from a draft, or edited since the last generation.
  // The last result left untouched is not their work — re-running over it (a
  // new horizon, the other scope) costs nothing, so the renderer asks only
  // when this is true.
  const generateWouldDiscard = selectedLines.length > 0
    && selectedLines !== suggestedLines;

  const isValid = !!(
    supplierId && locationId && selectedLines.length > 0
    && supplierMismatchItems.length === 0
    && (!isMismatchedCcy || (autoFx ? !!fx?.rate : typedRate > 0))
  );

  // -------------------------------------------------------------------------
  // Save. `lockRate` also stores the typed rate as the supplier's default —
  // after the order is saved, so a refused order leaves the supplier as it was.
  // `updateCosts` is the merchant's answer to the cost question, sent only
  // once it was asked: a line price that is not the supplier's current price
  // becomes that supplier's price (and the item cost, where the supplier is the
  // default) only on a yes. The server asks — it alone knows the supplier's
  // current price — by answering the save with `requires_confirmation` and
  // the `cost_changes` it would make, having written nothing
  // (po_viewset._plan_amended_costs).
  // -------------------------------------------------------------------------
  const save = useCallback(async (targetStatus, { lockRate = false, updateCosts } = {}) => {
    setSavingAs(targetStatus);
    setError(null);

    try {
      const payload = {
        supplier_id: supplierId,
        location_id: locationId,
        // The proposed date is not sent: without a date the server dates
        // each line by its own item's lead time, and the proposed date is
        // only the latest of those.
        need_by_date: needByDate === proposedNeedByDate ? null : needByDate,
        notes,
        supplier_notes: supplierNotes,
        reference_number: referenceNumber,
        tax_inclusive: taxInclusive,
        status: targetStatus,
        lines: selectedLines.map(line => ({
          item_id: line.item_id,
          quantity: line.quantity,
          cost: line.cost ?? null,
          tax_rate_percentage: line.tax_rate_percentage,
          supplier_sku: line.supplier_sku,
        })),
      };
      if (recommendationId && !purchaseOrderId) {
        payload.from_recommendation_id = recommendationId;
        payload.excluded_line_ids = excludedLineIds;
      }
      if (updateCosts !== undefined) payload.update_costs = updateCosts;
      if (isMismatchedCcy) {
        if (autoFx && fx?.rate) {
          // Leave exchange_rate null — server reads the cache.
        } else if (!autoFx) {
          if (isNaN(typedRate) || typedRate <= 0) {
            setError(t('purchaseOrders.create.manualRateRequired'));
            setSavingAs(null);
            return;
          }
          payload.exchange_rate = typedRate;
          payload.exchange_rate_source = 'manual';
        }
      }

      // Edit mode → POST /update_draft/ (full line replacement on an
      // existing draft PO). Otherwise → POST / (create). The payload shape
      // is identical; only the URL differs.
      const data = await apiJson.post(
        purchaseOrderId
          ? `/api/po-calculations/purchase-orders/${purchaseOrderId}/update_draft/`
          : '/api/po-calculations/purchase-orders/',
        payload,
      );
      if (data.requires_confirmation) {
        // The question IS its rows: none, or asked again after an answer, is
        // a broken server contract — never an empty dialog to click through.
        if (updateCosts !== undefined || !Array.isArray(data.cost_changes)
            || data.cost_changes.length === 0) {
          throw new Error(`cost question unusable: ${JSON.stringify(data)}`);
        }
        setCostQuestion({ targetStatus, lockRate, changes: data.cost_changes });
        return;
      }
      const messages = [];
      if (data.auto_created_item_locations?.length) {
        messages.push(t('purchaseOrders.create.autoCreatedItemLocations', {
          count: data.auto_created_item_locations.length,
          items: data.auto_created_item_locations.join(', '),
        }));
      }
      // A refused lock does not undo the saved order: the merchant reads it
      // with the save's other notes and can lock the rate in Supplier Setup.
      if (lockRate) {
        try {
          await apiJson.patch(`/api/suppliers/${supplierId}/`, { fixed_fx_rate: typedRate });
          changed([DATA.suppliers]);
        } catch (err) {
          console.error('Lock supplier rate failed:', err.message);
          messages.push(t('purchaseOrders.create.lockRatePrompt.lockFailed'));
        }
      }
      // Close FIRST: the caller opens the new PO's tab, and the merchant
      // must not come back to this form still standing open. The shell hides
      // a tab's overlays with the tab, so the close and the switch may batch
      // (tabs/TabOverlayScope.jsx, TabOverlayScope.test.jsx).
      onClose();
      onSaved({
        id: data.purchase_order_header_id,
        poNumber: data.po_number,
        status: targetStatus,
        messages,
      });
    } catch (err) {
      // A DRF ValidationError is an uncoded 400 (e.g. "lines: required",
      // "supplier: invalid") — flattened, it names the field to fix. Any
      // other uncoded body is the server's own English, never shown.
      const fieldErrors = err.status === 400 && !err.code ? formatApiError(err.body) : '';
      if (fieldErrors) {
        console.error('PO save validation failed:', fieldErrors);
        setError(fieldErrors);
      } else {
        console.error('PO save failed:', err.message);
        setError(translateRequestError(err, t, 'purchaseOrders.create.saveFailed'));
      }
    } finally {
      setSavingAs(null);
    }
  }, [
    supplierId, locationId, needByDate, proposedNeedByDate, notes, supplierNotes, referenceNumber,
    taxInclusive, selectedLines, recommendationId, excludedLineIds,
    purchaseOrderId,
    isMismatchedCcy, autoFx, fx, typedRate, onSaved, onClose, t,
    changed,
  ]);

  // The merchant's answer to the cost question: true / false re-sends the
  // asked save with it, null goes back to the form with nothing saved.
  const answerCostQuestion = useCallback(async (updateCosts) => {
    const asked = costQuestion;
    setCostQuestion(null);
    if (updateCosts === null) return;
    await save(asked.targetStatus, { lockRate: asked.lockRate, updateCosts });
  }, [costQuestion, save]);

  return {
    // --- state (read) ---
    state: {
      supplierId, locationId,
      // What the date field shows: the merchant's date, else the proposed one.
      needByDate: needByDate ?? proposedNeedByDate,
      needByDateError: proposedNeedByError,
      notes, supplierNotes, referenceNumber,
      taxInclusive, selectedLines,
      suppliers, suppliersLoading, suppliersError, suppliersReady,
      locations, locationsError,
      fx, fxLoading, fxError, autoFx, manualRate,
      saving, savingAs, error, costQuestion,
      poNumber,
      suggestionDays, suggestionScope, suggesting, suggestion,
    },
    // --- state setters (controlled inputs) ---
    actions: {
      setSupplierId: chooseSupplier,
      setLocationId,
      setNeedByDate: chooseNeedByDate,
      setNotes,
      setSupplierNotes,
      setReferenceNumber,
      setTaxInclusive,
      setSelectedLines,
      setAutoFx,
      setManualRate,
      setError,
      setSuggestionDays,
      setSuggestionScope,

      // operational
      loadFx,
      applyPickedRows,
      generateLinesFromSales,
      removeLine,
      updateLine,
      save,
      answerCostQuestion,
    },
    // --- derived ---
    derived: {
      selectedSupplier,
      storeCurrency,
      supplierCurrencyCode,
      supplierCurrencySymbol,
      isMismatchedCcy,
      subtotal,
      taxTotal,
      hasTaxedLine,
      total,
      activeRate,
      storeTotal,
      unpricedLineCount,
      orderRuleLineCount,
      supplierMismatchItems,
      offerLock,
      isValid,
      generateWouldDiscard,
    },
  };
}

